import React, { useEffect, useRef, useState } from 'react';
import { arrayOf, func, string } from 'prop-types';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';

import { FormattedMessage } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import {
  getAlgoliaLookingSimilarClient,
  isClientSideAlgoliaConfigured,
  searchAlgoliaListingsClient,
} from '../../util/algoliaSearch';
import { createListingFromAlgoliaHit } from '../../util/algoliaListings';
import ListingCard from '../../components/ListingCard/ListingCard';

import css from './ListingPage.module.css';

const MAX_SIMILAR_PRODUCTS = 8;
const SCROLL_CARD_COUNT = 2;

const listingFilterParams = publicData => ({
  ...(publicData.department ? { pub_department: publicData.department } : {}),
  ...(publicData.category ? { pub_category: publicData.category } : {}),
  ...(publicData.subCategory ? { pub_subCategory: publicData.subCategory } : {}),
  ...(publicData.designer ? { pub_designer: publicData.designer } : {}),
});

const SimilarListingsMaybe = props => {
  const { listing, currentUser, favorites, toggleFavoriteListingId, onToggleFavorite } = props;
  const [similarListings, setSimilarListings] = useState([]);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const scrollerRef = useRef(null);

  const listingId = listing?.id?.uuid;
  const publicData = listing?.attributes?.publicData || {};

  useEffect(() => {
    if (!listingId || !isClientSideAlgoliaConfigured()) {
      setSimilarListings([]);
      return undefined;
    }

    const abortController = new AbortController();
    const createListings = hits =>
      (hits || [])
        .filter(hit => hit?.objectID && hit.objectID !== listingId)
        .slice(0, MAX_SIMILAR_PRODUCTS)
        .map(createListingFromAlgoliaHit);

    const fetchFallbackListings = () =>
      searchAlgoliaListingsClient({
        ...listingFilterParams(publicData),
        hitsPerPage: MAX_SIMILAR_PRODUCTS + 1,
        signal: abortController.signal,
      }).then(data => createListings(data?.hits));

    getAlgoliaLookingSimilarClient({
      objectID: listingId,
      maxRecommendations: MAX_SIMILAR_PRODUCTS,
      signal: abortController.signal,
      queryParameters: {
        facetFilters: ['state:published', 'inStock:true'],
      },
      fallbackParameters: {
        facetFilters: [
          'state:published',
          'inStock:true',
          publicData.department ? `department:${publicData.department}` : null,
          publicData.category ? `category:${publicData.category}` : null,
        ].filter(Boolean),
      },
    })
      .then(hits => {
        const listings = createListings(hits);
        return listings.length ? listings : fetchFallbackListings();
      })
      .then(listings => {
        setSimilarListings(listings || []);
      })
      .catch(e => {
        if (e?.name !== 'AbortError') {
          fetchFallbackListings()
            .then(listings => setSimilarListings(listings || []))
            .catch(() => setSimilarListings([]));
        }
      });

    return () => abortController.abort();
  }, [
    listingId,
    publicData.department,
    publicData.category,
    publicData.subCategory,
    publicData.designer,
  ]);

  const updateScrollButtons = () => {
    const scroller = scrollerRef.current;
    if (!scroller) {
      return;
    }

    setCanScrollLeft(scroller.scrollLeft > 0);
    setCanScrollRight(scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 1);
  };

  useEffect(() => {
    updateScrollButtons();
    window.addEventListener('resize', updateScrollButtons);
    return () => window.removeEventListener('resize', updateScrollButtons);
  }, [similarListings.length]);

  const scrollSimilarListings = direction => {
    const scroller = scrollerRef.current;
    if (!scroller) {
      return;
    }

    const firstCard = scroller.querySelector(`.${css.similarListingCard}`);
    const cardWidth = firstCard?.getBoundingClientRect().width || 0;
    const columnGap = parseFloat(window.getComputedStyle(scroller).columnGap) || 0;
    const cardStep = cardWidth > 0 ? cardWidth + columnGap : scroller.clientWidth / 2;
    const scrollAmount = direction * cardStep * SCROLL_CARD_COUNT;

    scroller.scrollBy({ left: scrollAmount, behavior: 'smooth' });
  };

  if (!similarListings.length) {
    return null;
  }

  return (
    <section className={css.similarListingsSection}>
      <div className={css.similarListingsHeader}>
        <h2 className={css.similarListingsTitle}>
          <FormattedMessage
            id="ListingPage.similarProductsTitle"
            defaultMessage="You May Also Like"
          />
        </h2>
        <div className={css.similarListingsControls}>
          <button
            className={css.similarListingsArrow}
            type="button"
            aria-label="Scroll similar products left"
            disabled={!canScrollLeft}
            onClick={() => scrollSimilarListings(-1)}
          >
            <FiChevronLeft />
          </button>
          <button
            className={css.similarListingsArrow}
            type="button"
            aria-label="Scroll similar products right"
            disabled={!canScrollRight}
            onClick={() => scrollSimilarListings(1)}
          >
            <FiChevronRight />
          </button>
        </div>
      </div>
      <div className={css.similarListingsScroller} ref={scrollerRef} onScroll={updateScrollButtons}>
        {similarListings.map(similarListing => {
          const id = similarListing.id.uuid;
          return (
            <ListingCard
              key={id}
              className={css.similarListingCard}
              listing={similarListing}
              currentUser={currentUser}
              isFavorited={favorites.includes(id)}
              isFavoriteLoading={toggleFavoriteListingId === id}
              onToggleFavorite={onToggleFavorite}
              renderSizes="(max-width: 767px) 50vw, 25vw"
            />
          );
        })}
      </div>
    </section>
  );
};

SimilarListingsMaybe.defaultProps = {
  currentUser: null,
  favorites: [],
  toggleFavoriteListingId: null,
  onToggleFavorite: null,
};

SimilarListingsMaybe.propTypes = {
  listing: propTypes.listing.isRequired,
  currentUser: propTypes.currentUser,
  favorites: arrayOf(string),
  toggleFavoriteListingId: string,
  onToggleFavorite: func,
};

export default SimilarListingsMaybe;
